import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaMicrophone, FaPaperPlane, FaRobot, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { API_ENDPOINTS } from "../config/constants";

const welcome = {
  role: "assistant",
  text: "Namaste! Main aapka AI Restaurant Manager hoon. Jaise: \"Table 4 mein 10 tea add karo\" ya \"2 masala dosa quick order mein add karo\".",
};

const cleanProposal = (proposal, products, variantsByProduct, addonsByProduct) => (Array.isArray(proposal) ? proposal : [])
  .map((item) => {
    const product = products.find((entry) => String(entry.id) === String(item.product_id));
    const quantity = Math.min(999, Math.max(1, Number(item.quantity) || 0));
    if (!product || !quantity) return null;
    const variant = (variantsByProduct?.[product.id] || []).find((entry) => String(entry.id) === String(item.variant_id));
    const addons = (addonsByProduct?.[product.id] || []).filter((entry) =>
      (item.addon_ids || []).map(String).includes(String(entry.id))
    );
    return {
      product_id: product.id,
      name: product.name,
      quantity,
      variant_id: variant?.id ?? null,
      variant_label: variant?.label || "",
      addon_ids: addons.map((addon) => addon.id),
      addon_labels: addons.map((addon) => addon.name),
    };
  })
  .filter(Boolean);

export default function RestaurantAiAssistant({ restaurantId, products, variantsByProduct, addonsByProduct, currentTarget, onConfirm, onHide }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([welcome]);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryStatus, setRetryStatus] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [speechMessage, setSpeechMessage] = useState("");
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechBaseInputRef = useRef("");
  const history = useMemo(() => messages.slice(-8).map(({ role, text }) => ({ role, content: text })), [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => () => recognitionRef.current?.stop?.(), []);

  const toggleSpeechRecognition = () => {
    if (isListening) {
      recognitionRef.current?.stop?.();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechMessage("Voice input is not supported in this browser. Please use Chrome or the latest desktop app.");
      return;
    }
    setSpeechMessage("");
    speechBaseInputRef.current = input.trim();
    const recognition = new SpeechRecognition();
    // en-IN favors Roman/Hinglish transcripts (e.g. "10 tea table 4 mein")
    // rather than returning spoken Hindi in Devanagari script.
    recognition.lang = "en-IN";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      let transcript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        transcript += event.results[index][0].transcript;
      }
      setInput([speechBaseInputRef.current, transcript.trim()].filter(Boolean).join(" "));
    };
    recognition.onerror = (event) => {
      const messageByError = {
        "not-allowed": "Microphone permission is required for voice input.",
        "no-speech": "No speech heard. Please try again.",
        "audio-capture": "No microphone was found.",
      };
      setSpeechMessage(messageByError[event.error] || "Voice input could not start. Please try again.");
    };
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  };

  const send = async (event) => {
    event?.preventDefault();
    const message = input.trim();
    if (!message || loading || !restaurantId) return;
    setInput("");
    setMessages((previous) => [...previous, { role: "user", text: message }]);
    setLoading(true);
    setRetryStatus("");
    try {
      const requestBody = JSON.stringify({ restaurantid: restaurantId, message, history, current_target: currentTarget });
      console.groupCollapsed("[AI Manager] Chat request");
      console.log("Message:", message);
      console.log("Target:", currentTarget);
      console.log("Available menu products:", products.map((product) => ({ id: product.id, name: product.name })));
      console.groupEnd();
      let data;
      let lastError;
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const response = await fetch(API_ENDPOINTS.AI_RESTAURANT_MANAGER, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: requestBody,
          });
          data = await response.json();
          if (!response.ok || !data?.success) throw new Error(data?.message || "AI manager could not process that request.");
          break;
        } catch (error) {
          lastError = error;
          if (attempt === maxAttempts) throw lastError;
          setRetryStatus(`AI response failed. Retrying automatically in 5 seconds (${attempt}/${maxAttempts})…`);
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
      setRetryStatus("");
      const proposal = cleanProposal(data.proposal, products, variantsByProduct, addonsByProduct);
      console.groupCollapsed("[AI Manager] Server response and validated proposal");
      console.log("AI reply:", data.reply);
      console.log("Raw server proposal:", data.proposal || []);
      console.log("Validated proposal to confirm:", proposal);
      console.log("Resolved target:", data.target || currentTarget);
      console.groupEnd();
      const nextPending = proposal.length ? { items: proposal, target: data.target || currentTarget } : null;
      setPending(nextPending);
      setMessages((previous) => [...previous, { role: "assistant", text: data.reply || "I could not find that item in your menu." }]);
    } catch (error) {
      console.error("[AI Manager] Chat request failed after retries:", error);
      setMessages((previous) => [...previous, { role: "assistant", text: error.message || "Could not connect to AI manager. Please try again." }]);
    } finally {
      setRetryStatus("");
      setLoading(false);
    }
  };

  const confirm = () => {
    if (!pending) return;
    console.info("[AI Manager] Operator confirmed proposal:", pending);
    onConfirm(pending);
    const itemText = pending.items.map((item) => `${item.quantity} ${item.name}`).join(", ");
    setMessages((previous) => [...previous, { role: "assistant", text: `${itemText} cart mein add kar diya gaya.` }]);
    setPending(null);
  };

  return <>
    {!open && <LauncherGroup>
      <Launcher type="button" onClick={() => setOpen(true)} aria-label="Open AI Restaurant Manager">
        <FaRobot /><span>AI Manager</span>
      </Launcher>
      <HideLauncher
        type="button"
        onClick={onHide}
        aria-label="Hide AI Manager"
        title="Hide AI Manager. Re-enable it from POS Settings."
      >
        <FaTimes />
      </HideLauncher>
    </LauncherGroup>}
    {open && <Panel role="dialog" aria-label="AI Restaurant Manager">
      <PanelHeader>
        <div><FaRobot /><div><strong>AI Restaurant Manager</strong><small>Menu-aware • confirmation required</small></div></div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close AI manager"><FaTimes /></button>
      </PanelHeader>
      <Target>Adding to: <b>{currentTarget?.label || "Current order"}</b></Target>
      <Conversation aria-live="polite">
        {messages.map((message, index) => <Bubble key={`${message.role}-${index}`} $user={message.role === "user"}>{message.text}</Bubble>)}
        {loading && <Bubble>Checking your menu…</Bubble>}
      </Conversation>
      {retryStatus && <RetryNotice role="status">{retryStatus}</RetryNotice>}
      {pending && <Confirmation>
        <strong>Confirm before adding</strong>
        {pending.items.map((item) => <span key={`${item.product_id}-${item.variant_id || "base"}-${item.addon_ids.join("-")}`}>
          {item.quantity} × {item.name}{item.variant_label ? ` (${item.variant_label})` : ""}{item.addon_labels.length ? ` + ${item.addon_labels.join(", ")}` : ""}
        </span>)}
        <div><button type="button" onClick={() => setPending(null)}>Cancel</button><button type="button" onClick={confirm}><FaCheck /> Confirm & add</button></div>
      </Confirmation>}
      <ChatForm onSubmit={send}>
        <input ref={inputRef} value={input} onChange={(event) => setInput(event.target.value)} placeholder="e.g. 10 tea add karo" aria-label="Message AI restaurant manager" />
        <button type="button" onClick={toggleSpeechRecognition} className={isListening ? "listening" : ""} aria-label={isListening ? "Stop voice input" : "Start voice input"} title={isListening ? "Listening… tap to stop" : "Speak your order"}><FaMicrophone /></button>
        <button type="submit" disabled={!input.trim() || loading} aria-label="Send message"><FaPaperPlane /></button>
      </ChatForm>
      {speechMessage && <SpeechNotice role="status">{speechMessage}</SpeechNotice>}
    </Panel>}
  </>;
}

const LauncherGroup = styled.div`
  position: fixed; right: 84px; bottom: 22px; z-index: 70; display: flex; align-items: center; gap: 7px;
  @media (max-width: 640px) { right: 78px; bottom: 18px; }
`;
const Launcher = styled.button`
  min-height: 46px; padding: 0 16px; border: 0; border-radius: 999px;
  background: #0f766e; color: #fff; font-weight: 800; display: flex; gap: 8px; align-items: center; cursor: pointer; box-shadow: 0 12px 28px rgba(15,118,110,.32);
  &:hover { background: #115e59; transform: translateY(-1px); }
  &:focus-visible { outline: 3px solid rgba(15,118,110,.28); outline-offset: 3px; }
`;
const HideLauncher = styled.button`
  width: 44px; height: 44px; flex: 0 0 44px; border: 1px solid #cbd5e1; border-radius: 999px; background: #fff; color: #475569;
  display: grid; place-items: center; cursor: pointer; box-shadow: 0 10px 24px rgba(15,23,42,.18);
  &:hover { background: #f8fafc; color: #b91c1c; border-color: #fca5a5; }
  &:focus-visible { outline: 3px solid rgba(15,118,110,.24); outline-offset: 3px; }
`;
const Panel = styled.section`
  position: fixed; right: 22px; bottom: 78px; z-index: 71; width: min(390px, calc(100vw - 28px)); height: min(580px, calc(100dvh - 100px)); background: #fff; border: 1px solid #cbd5e1; border-radius: 16px; box-shadow: 0 24px 64px rgba(15,23,42,.26); display:flex; flex-direction:column; overflow:hidden;
  @media (max-width:640px) { right:14px; bottom:70px; }
`;
const PanelHeader = styled.header`
  padding: 14px 16px; background:#0f766e; color:#fff; display:flex; align-items:center; justify-content:space-between; > div { display:flex; gap:10px; align-items:center; } svg { font-size:20px; } strong, small { display:block; } small { font-size:11px; opacity:.82; margin-top:2px; } button { color:#fff; background:transparent; border:0; min-width:44px; min-height:44px; cursor:pointer; }
`;
const Target = styled.div`padding:8px 14px; font-size:12px; color:#475569; background:#f0fdfa; border-bottom:1px solid #ccfbf1;`;
const Conversation = styled.div`flex:1; overflow:auto; padding:14px; display:flex; flex-direction:column; gap:10px; background:#f8fafc;`;
const Bubble = styled.div`
  max-width:86%; align-self:${p => p.$user ? "flex-end" : "flex-start"}; padding:10px 12px; border-radius:12px; white-space:pre-wrap; font-size:14px; line-height:1.42;
  background:${p => p.$user ? "#0f766e" : "#fff"}; color:${p => p.$user ? "#fff" : "#1e293b"}; box-shadow:0 1px 2px rgba(15,23,42,.08);
`;
const Confirmation = styled.div`
  margin:0 12px 10px; padding:11px; background:#fffbeb; border:1px solid #fcd34d; border-radius:10px; color:#78350f; font-size:13px; strong, span { display:block; margin-bottom:5px; } div { display:flex; gap:8px; margin-top:9px; } button { min-height:38px; border-radius:8px; border:1px solid #d97706; background:#fff; color:#92400e; font-weight:800; padding:0 10px; cursor:pointer; } button:last-child { background:#d97706; color:#fff; display:flex; gap:6px; align-items:center; }
`;
const ChatForm = styled.form`
  display:flex; gap:8px; padding:12px; border-top:1px solid #e2e8f0; input { min-width:0; flex:1; min-height:44px; border:1px solid #94a3b8; border-radius:9px; padding:0 11px; font-size:16px; } button { width:44px; min-width:44px; border:0; border-radius:9px; background:#0f766e; color:#fff; cursor:pointer; } button.listening { background:#b91c1c; box-shadow:0 0 0 3px rgba(185,28,28,.18); } button:disabled { opacity:.45; cursor:not-allowed; }
`;
const SpeechNotice = styled.p`
  margin: -4px 12px 10px; color: #b45309; font-size: 12px; line-height: 1.35;
`;
const RetryNotice = styled.p`
  margin: 0 12px 10px; color: #9a3412; font-size: 12px; font-weight: 700; line-height: 1.35;
`;
